import { client } from '@api/client';

import { IAlarmManagement, IAlarmOran, IAlarmsFlexidas } from '@interfaces/alarms/alarm';
import {
	IAlarmCreateNoVeRuleDTO,
	IGetAlarmDTO,
	IGetAlarmManagementFlexidasDTO,
	TAlarmUpdateNoVeRuleDTO,
} from '@interfaces/alarms/dto/alarmsDTO';
import { IId, TIdName, TNameTitle } from '@interfaces/common/common';
import { IExportDefaultDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';

import { TVendorsValues } from '@enums/common';

import { URL } from './urls';

const {
	alarmManagementTable,
	alarmsVendors,
	alarmManagementTableFlexidas,
	alarmManagementTableOran,
	alarmManagementTableBenetel,
	alarmManagementNoVesNetworkElements,
	alarmManagementNoFileNetworkElements,
	alarmManagementAddNoVesRule,
	alarmManagementNoVesEvent,
	alarmManagementBenetelExport,
	alarmManagementBenetelNoVesEvent,
	alarmManagementBenetelNoVesAddRule,
	alarmManagementNoFileAddRule,
	alarmManagementNoFile,
	alarmManagementBenetelNoVesNetworkElements,
	alarmBenetelStaticAlarmIds,
	alarmManagementComplaint,
} = URL;

export const getAlarm = ({
	pageSize,
	page,
	sortDirection,
	sortFieldName,
	searchValue,
	isActive,
	group,
	severity,
}: IGetAlarmDTO): Promise<IItemsResponse<IAlarmManagement>> => {
	return client.get(alarmManagementTable, {
		params: {
			pageSize,
			page,
			sortDirection,
			sortFieldName,
			searchValue,
			isEnabled: isActive,
			group,
			severity,
		},
	});
};

export const getAlarmOran = ({ id }: IId): Promise<IAlarmOran> => {
	return client.get(`${alarmManagementTableOran}/${id}`);
};

export const getAlarmsOran = (
	data: IGetAlarmManagementFlexidasDTO,
): Promise<IItemsResponse<IAlarmOran>> => {
	return client.get(alarmManagementTableOran, {
		params: data,
	});
};

export const getAlarmsFlexidas = (
	data: IGetAlarmManagementFlexidasDTO,
): Promise<IItemsResponse<IAlarmsFlexidas>> => {
	return client.get(alarmManagementTableFlexidas, {
		params: data,
	});
};

export const getAlarmsBenetel = (
	data: IGetAlarmManagementFlexidasDTO,
): Promise<IItemsResponse<IAlarmOran>> => {
	return client.get(alarmManagementTableBenetel, {
		params: data,
	});
};

export const getBenetelAlarm = ({ id }: IId): Promise<IAlarmOran> => {
	return client.get(`${alarmManagementTableBenetel}/${id}`);
};

export const exportBenetelAlarms = (dto: IExportDefaultDTO): Promise<void> => {
	return client.post(alarmManagementBenetelExport, dto);
};

export const getAlarmsVendors = (): Promise<TNameTitle<TVendorsValues>[]> => {
	return client.get(alarmsVendors);
};

export const getAlarmsNoVesNetworkElements = (): Promise<TIdName[]> => {
	return client.get(alarmManagementNoVesNetworkElements);
};

export const getAlarmsNoFileNetworkElements = (): Promise<TIdName[]> => {
	return client.get(alarmManagementNoFileNetworkElements);
};

export const exportAlarmManagementOran = (dto: IExportDefaultDTO): Promise<void> => {
	return client.post(`${alarmManagementTableOran}/export`, dto);
};

export const createAlarmNoVesRule = (dto: IAlarmCreateNoVeRuleDTO): Promise<void> => {
	return client.post(alarmManagementAddNoVesRule, dto);
};

export const createAlarmNoVesRules = (dto: IAlarmCreateNoVeRuleDTO[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => createAlarmNoVesRule(item)));
};

export const updateAlarmNoVesRule = ({ id, ...dto }: TAlarmUpdateNoVeRuleDTO): Promise<void> => {
	return client.put(`${alarmManagementNoVesEvent}${id}/update-rule`, dto);
};

export const updateAlarmNoVesRules = (dto: TAlarmUpdateNoVeRuleDTO[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => updateAlarmNoVesRule(item)));
};

export const deleteAlarmNoVesRule = ({ id }: IId): Promise<void> => {
	return client.delete(`${alarmManagementNoVesEvent}${id}/delete-rule`);
};

export const deleteAlarmNoVesRules = (dto: IId[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => deleteAlarmNoVesRule(item)));
};

export const getBenetelNoVesNetworkElements = (): Promise<TIdName[]> => {
	return client.get(alarmManagementBenetelNoVesNetworkElements);
};

export const createBenetelNoVesRule = (dto: IAlarmCreateNoVeRuleDTO): Promise<void> => {
	return client.post(alarmManagementBenetelNoVesAddRule, dto);
};

export const createBenetelNoVesRules = (dto: IAlarmCreateNoVeRuleDTO[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => createBenetelNoVesRule(item)));
};

export const updateBenetelNoVesRule = ({ id, ...dto }: TAlarmUpdateNoVeRuleDTO): Promise<void> => {
	return client.put(`${alarmManagementBenetelNoVesEvent}${id}/update-rule`, dto);
};

export const updateBenetelNoVesRules = (dto: TAlarmUpdateNoVeRuleDTO[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => updateBenetelNoVesRule(item)));
};

export const deleteBenetelNoVesRule = ({ id }: IId): Promise<void> => {
	return client.delete(`${alarmManagementBenetelNoVesEvent}${id}/delete-rule`);
};

export const deleteBenetelNoVesRules = (dto: IId[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => deleteBenetelNoVesRule(item)));
};
export const createNoFileRule = (dto: IAlarmCreateNoVeRuleDTO): Promise<void> => {
	return client.post(alarmManagementNoFileAddRule, dto);
};

export const createNoFileRules = (dto: IAlarmCreateNoVeRuleDTO[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => createNoFileRule({
		...item,
		noFileMinutes: item.noEventMinutes,
		noEventMinutes: undefined,
	})));
};

export const updateNoFileRule = ({ id, ...dto }: TAlarmUpdateNoVeRuleDTO): Promise<void> => {
	return client.put(`${alarmManagementNoFile}${id}/update-rule`, dto);
};

export const updateNoFileRules = (dto: TAlarmUpdateNoVeRuleDTO[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => updateNoFileRule({
		...item,
		noFileMinutes: item.noEventMinutes,
		noEventMinutes: undefined,
	})));
};

export const deleteNoFileRule = ({ id }: IId): Promise<void> => {
	return client.delete(`${alarmManagementNoFile}${id}/delete-rule`);
};

export const deleteNoFileRules = (dto: IId[]): Promise<PromiseSettledResult<void>[]> => {
	return Promise.allSettled(dto.map((item) => deleteNoFileRule(item)));
};

export const getBenetelAlarmIds = (): Promise<Array<TNameTitle>> => {
	return client.get(alarmBenetelStaticAlarmIds);
};

export const getComplaintAlarms = (params: IGetAlarmManagementFlexidasDTO): Promise<IItemsResponse<IAlarmOran>> => {
	return client.get(alarmManagementComplaint, { params });
};

